/**
 * enginePreProcessJson_Merge.ts - Core template merging with slots for EnginePreProcessJson
 * Pure ES module - works in both Node.js and browser via bundler
 */

import { LogLevel, Logger } from '../../../../Arshu/dist/logger/logger.js';
import { mergeDataJsonIntoHtmlJson, mergeContextIntoDataJson } from '../../base/engine/htmlJsonMergeUtil.js';
import { DynamicResultsJson } from '../../model/dynamicResultsJson.js';
import { MergeResult } from '../../../../Assembler/dist/model/mergeResult.js';
import { JsonObject, JsonValue } from '../../../../Assembler/dist/app/json/jsonValue.js';
import { CommonUtil } from '../../../../Assembler/dist/common/commonUtil.js';
import { TransientStore } from '../../../../Assembler/dist/registry/transientStore.js';
import { OperationsMetadata as OperationsMetadataClass } from '../../../../Assembler/dist/model/operationsMetadata.js';
import { mergeTemplate } from './enginePreProcessJson_Merge_TemplateTraversal.js';
import { TemplateInfoNode, TemplateOperation, AppSiteMetadataRef } from './enginePreProcessJson_Helper.js';
import { msToNanos } from './enginePreProcessJson_Merge_Constants.js';

import type { EnginePreProcessJson, ILoaderPreProcessJson } from './enginePreProcessJson.js';

/**
 * Bundles traversal-state params for slot traversal helpers so the function
 * stays under the 18-param polyglot arity ceiling.
 */
export interface MergeTraversalContext {
    recursionDepth: number;
    flowStack: string[] | null;
    contextJson: JsonObject | null;
    slotName: string | null;
    configJson: JsonObject | null;
}

/**
 * AM-001: merge-invariant traversal context for the PreProcessJson engine. Bundles the values
 * that are fixed for one MergeTemplates call and travel unchanged through every traversal helper
 * (mergeTemplate, placeholder/slotholder/array/conditional processing). Per-call-variant values
 * (currentTemplate, templateKey, operations sink, jsonData, componentHtml, flowStack,
 * recursionDepth) stay as positional parameters. Mirrors the C# PreProcessJsonMergeContext
 * record (RootComponent, AppSiteName, Metadata, GenComponentHtml, ContextJson, ConfigJson).
 */
export interface PreProcessJsonMergeContext {
    rootComponent: TemplateInfoNode;
    appSiteName: string;
    metadata: AppSiteMetadataRef;
    genComponentHtml: boolean;
    contextJson: JsonObject | null;
    configJson: JsonObject | null;
    clientMode: boolean;
}

/**
 * Merges templates using metadata and returns componentHtml Map and optional operations
 * Uses the loader provided at construction time.
 * Returns {componentHtml: Map, operations} tuple for consistency with other engines
 */
export function mergeTemplates(
    this: EnginePreProcessJson,
    appSite: string,
    appView: string | null,
    genOperationJson: boolean,
    genComponentHtml: boolean,
    contextJson: JsonObject | null,
    configJson: JsonObject | null = null,
    actionMode: boolean = false,
    clientMode: boolean = false
): MergeResult {
    const engine = this;

    Logger.flow(`MergeTemplates: enter appSite=${appSite} appView=${appView || ''} actionMode=${actionMode}`, "PreProcessJson.MergeLifecycle");
    // AppSite-triggered resolution (loader-owned): an IAppSite handler may redefine the AppSite/AppView
    // before any appfile/template resolution. Unchanged when none applies.
    const appSiteResolved = engine._loader!.applyDynamicAppSiteHandlers(appSite, appView, contextJson ?? new JsonObject(), configJson);
    appSite = appSiteResolved.appSite;
    appView = appSiteResolved.appView;
    // Drain the per-merge navigation trace (ordered IAppSite hops) accumulated at the merge top.
    const navigationTrace = engine._loader!.getAndClearNavigationTrace();
    // Resolve appFile and validate appView via loader
    const appFileResult = engine._loader!.getAppFile(appSite, appView || '');
    const appFile = appFileResult.appFile;
    appView = appFileResult.validatedAppView;
    Logger.flow(`MergeTemplates: appFile=${appFile}`, "PreProcessJson.MergeLifecycle");
    const { mergeContextJson, mergeConfigJson } = engine.prepareProtectedMergeContext(appSite, appView, appFile, contextJson, configJson);

    if (Logger.getLogLevel() <= LogLevel.DEBUG) {
        Logger.debug(`MergeTemplates called: appSite=${appSite}, appFile=${appFile}, appView=${appView || 'null'}, actionMode=${actionMode}`, 'EnginePreProcessJson');
    }

    // Create fresh DynamicResultsJson per merge; set actionMode/transient state and mark merge state.
    const dynamicResultsJson = engine.initializeDynamicMergeState(appSite, appFile, appView, actionMode);

    // Initialize componentHtml Map
    const componentHtml = new JsonObject();

    try {
        // STEP 1: Get AppViews list from metadata
        const appViewNames: string[] = [];
        const baseMetadata = engine._loader!.getAppSiteMetadata(appSite, null);

        if (baseMetadata) {
            appViewNames.push(...(baseMetadata.appViews || []));
        } else if (appView) {
            const appViewMetadata = engine._loader!.getAppSiteMetadata(appSite, appView);
            if (appViewMetadata) {
                appViewNames.push(...(appViewMetadata.appViews || []));
            }
        }

        // STEP 2: Process based on whether we have AppViews
        let result: { html: string; operations: OperationsMetadataClass | null };
        if (appViewNames.length > 0) {
            // CASE A: AppViews exist
            const targetAppView = appView || appViewNames[0];
            const metadata = engine._loader!.getAppSiteMetadata(appSite, targetAppView);

            if (!metadata) {
                Logger.warn(`AppView metadata not found: ${appSite}_${targetAppView}`, 'EnginePreProcessJson');
                componentHtml.set(appFile, engine._loader!.defaultReplaceHtml);
                const noMetaResult = new MergeResult(appFile, componentHtml);
                noMetaResult.resolvedAppSite = appSiteResolved.appSite;
                noMetaResult.resolvedAppView = appSiteResolved.appView;
                noMetaResult.navigationTrace = navigationTrace;
                return noMetaResult;
            }

            result = engine._mergeWithMetadata(appSite, appFile, metadata, componentHtml, genOperationJson, genComponentHtml, mergeContextJson, mergeConfigJson, clientMode);
        } else {
            // CASE B: No AppViews - use base metadata
            if (!baseMetadata) {
                Logger.warn(`Base metadata not found: ${appSite}`, 'EnginePreProcessJson');
                componentHtml.set(appFile, engine._loader!.defaultReplaceHtml);
                const noBaseResult = new MergeResult(appFile, componentHtml);
                noBaseResult.resolvedAppSite = appSiteResolved.appSite;
                noBaseResult.resolvedAppView = appSiteResolved.appView;
                noBaseResult.navigationTrace = navigationTrace;
                return noBaseResult;
            }

            result = engine._mergeWithMetadata(appSite, appFile, baseMetadata, componentHtml, genOperationJson, genComponentHtml, mergeContextJson, mergeConfigJson, clientMode);
        }

        // Collect dynamic data/view/action results from the engine-owned DynamicResultsJson.
        const { dynamicData, dynamicView, dynamicAction, dynamicClientData, dynamicClient } = engine.collectDynamicHandlerResults(dynamicResultsJson);

        // If merge produced empty output (e.g., empty template), use defaultReplaceHtml
        const finalHtml = (!result.html || result.html.length === 0) ? engine._loader!.defaultReplaceHtml : result.html;
        // Server generation (§2B.4): join the Embedded client-data islands into the FINAL page HTML as a
        // SEPARATE DataHtml — one island per component keyed by component name, sourced from the raw the
        // preparers recorded — inserted before the last </body> (or appended for a fragment), never baked into
        // the reusable component HtmlJson, so the client can reconstruct the clean component and re-attach the
        // island reproducibly.
        componentHtml.set(appFile, CommonUtil.appendClientDataIslands(finalHtml, dynamicResultsJson.dynamicClientDataResults));
        const mergeResult = new MergeResult(appFile, componentHtml, result.operations, dynamicData, dynamicView, dynamicAction);
        mergeResult.dynamicClientData = dynamicClientData;
        mergeResult.dynamicClient = dynamicClient;
        mergeResult.resolvedAppSite = appSiteResolved.appSite;
        mergeResult.resolvedAppView = appSiteResolved.appView;
        mergeResult.navigationTrace = navigationTrace;
        Logger.flow(`MergeTemplates: exit components=${componentHtml.size}`, "PreProcessJson.MergeLifecycle");
        return mergeResult;
    } catch (ex) {
        Logger.error(`Failed to merge templates: ${(ex as Error).message}`, 'EnginePreProcessJson');
        Logger.fallback("F-ENG1", `merge exception surfaced: appSite=${appSite} appFile=${appFile} ex=${(ex as Error).message}`);
        throw ex;
    } finally {
        // Always clear action mode, even on early returns and exceptions
        engine._loader!.setActionMode(false);
    }
}

export function createProtectedMergeContext(this: EnginePreProcessJson, appSite: string, appView: string | null, appFile: string, engineType: string, contextJson: JsonObject): JsonObject {
    // Build a FRESH immutable merge context. The identity keys are written first onto the new (always
    // mutable) object, so the write can never be silently dropped by a read-only source — that silent
    // drop is the leak that let a stale/blank EngineType survive in some paths/langs. The caller's keys
    // are then copied in only where they don't collide, so the caller can never override
    // AppSite/AppView/AppFile/EngineType. The passed contextJson is never mutated (its values are
    // cloned, nothing is stamped onto it, it is never locked), so the same instance flows unchanged into
    // every engine and serializes plainly into the api-json (no leaked EngineType to diverge across
    // engines or config sources).
    const mergeContextJson = new JsonObject();
    mergeContextJson.set('AppSite', JsonValue.string(appSite));
    mergeContextJson.set('AppView', JsonValue.string(appView || ''));
    mergeContextJson.set('AppFile', JsonValue.string(appFile));
    mergeContextJson.set('EngineType', JsonValue.string(engineType));
    for (const [key, value] of contextJson.entries()) {
        if (!mergeContextJson.containsKey(key)) {
            mergeContextJson.set(key, value.deepClone());
        }
    }
    mergeContextJson.isReadOnly = true;
    return mergeContextJson;
}

export function createProtectedMergeConfig(this: EnginePreProcessJson, configJson: JsonObject | null): JsonObject | null {
    if (configJson) {
        configJson.isReadOnly = true;
    }
    return configJson;
}

export function prepareProtectedMergeContext(this: EnginePreProcessJson, appSite: string, appView: string | null, appFile: string, contextJson: JsonObject | null, configJson: JsonObject | null): { mergeContextJson: JsonObject | null; mergeConfigJson: JsonObject | null } {
    return {
        mergeContextJson: contextJson ? this.createProtectedMergeContext(appSite, appView, appFile, 'PreProcessJson', contextJson) : null,
        mergeConfigJson: this.createProtectedMergeConfig(configJson)
    };
}

export function initializeDynamicMergeState(this: EnginePreProcessJson, appSite: string, appFile: string, appView: string | null, actionMode: boolean): DynamicResultsJson {
    // Create fresh DynamicResultsJson per merge and set on loader
    const dynamicResultsJson = new DynamicResultsJson();
    this._loader!.setDynamicResults(dynamicResultsJson);
    // Set actionMode on loader before template processing
    this._loader!.setActionMode(actionMode);
    const transientStore = new TransientStore();
    this._loader!.setTransientStore(transientStore);
    return dynamicResultsJson;
}

export function collectDynamicHandlerResults(this: EnginePreProcessJson, dynamicResultsJson: DynamicResultsJson): { dynamicData: JsonObject; dynamicView: JsonObject; dynamicAction: JsonObject; dynamicClientData: JsonObject; dynamicClient: JsonObject } {
    // Collect dynamic timing from loader and accumulate.
    this.accumulateDynamicDataTimingFromLoader();
    this.accumulateDynamicViewTimingFromLoader();
    this.accumulateDynamicActionTimingFromLoader();
    this.accumulateDynamicClientDataTimingFromLoader();

    // Read dynamic results directly from the per-merge DynamicResultsJson.
    const dynamicData = dynamicResultsJson.dynamicDataResults;
    const dynamicView = dynamicResultsJson.dynamicViewResults;
    const dynamicAction = dynamicResultsJson.dynamicActionResults;
    const dynamicClientData = dynamicResultsJson.dynamicClientDataResults;
    const dynamicClient = dynamicResultsJson.dynamicClientResults;

    return { dynamicData, dynamicView, dynamicAction, dynamicClientData, dynamicClient };
}

export function mergeTemplateRecursive(
    engine: EnginePreProcessJson,
    loader: ILoaderPreProcessJson,
    htmlJson: JsonObject,
    rootComponent: TemplateInfoNode,
    currentTemplate: TemplateInfoNode,
    appSite: string,
    metadata: AppSiteMetadataRef,
    operations: TemplateOperation[],
    jsonData: JsonObject,
    componentHtml: JsonObject,
    flowStack: string[] | null,
    genComponentHtml: boolean,
    contextJson: JsonObject | null = null,
    configJson: JsonObject | null = null,
    clientMode: boolean = false
): string {
    void loader;
    engine.genMergeMissingInfo = false;
    engine.replaceMissingInfo = '';
    engine.maxArrayOperationItems = engine._loader!.maxArrayOperationItems;

    // AM-001: bundle the merge-invariant values once; traversal helpers receive them as one context.
    const context: PreProcessJsonMergeContext = {
        rootComponent,
        appSiteName: appSite,
        metadata,
        genComponentHtml,
        contextJson,
        configJson,
        clientMode
    };

    return mergeTemplate(
        engine,
        engine._loader!,
        context,
        currentTemplate,
        htmlJson,
        jsonData,
        operations,
        false,
        componentHtml,
        0,
        flowStack
    );
}

/**
 * Core merge logic using metadata
 */
export function mergeWithMetadata(
    engine: EnginePreProcessJson,
    loader: ILoaderPreProcessJson,
    appSite: string,
    appFile: string,
    metadata: AppSiteMetadataRef,
    componentHtml: JsonObject,
    genOperationJson: boolean,
    genComponentHtml: boolean,
    contextJson: JsonObject | null = null,
    configJson: JsonObject | null = null,
    clientMode: boolean = false
): { html: string; operations: OperationsMetadataClass | null } {
    if (Logger.getLogLevel() <= LogLevel.DEBUG) {
        Logger.debug(`MergeWithMetadata: appSite=${appSite}`, 'EnginePreProcessJson');
    }

    if (!metadata || !metadata.rootComponent) {
        Logger.warn('No root component in metadata', 'EnginePreProcessJson');
        return { html: '', operations: null };
    }

    // Set engine instance properties for this merge pass
        engine.genMergeMissingInfo = false;
        engine.replaceMissingInfo = '';
        engine.maxArrayOperationItems = loader.maxArrayOperationItems;

        // Get root template HtmlJson and DataJson
        // Pass rootTemplateName twice: as currentTemplateName and as appFileOrPlaceholderOrSlotholderName
        const rootTemplateName = metadata.rootComponent.templateName;
        const rootTemplateAppSite = metadata.rootComponent.templateAppSite || appSite;

        // Create context stack for inheritance resolution (supports multi-parent scenarios)
        const flowStack = engine.createFlowStack(rootTemplateName);

        const startGetTemplate = performance.now();
        const result = loader.getTemplateWithView(appSite, rootTemplateName, rootTemplateName, metadata.appView, flowStack, configJson, null, clientMode);
        engine._getTemplateNanos += msToNanos(performance.now() - startGetTemplate);
        engine._getTemplateCount++;

        // Update flowStack with resolved template name for correct inheritance lookup
        if (result && result.resolvedTemplateName && result.resolvedTemplateName !== rootTemplateName && flowStack.length > 0) {
            flowStack[flowStack.length - 1] = result.resolvedTemplateName;
        }

        if (!result || !result.htmlJson) {
            Logger.flow(`MergeTemplates: mainTemplate=missing`, "PreProcessJson.MergeLifecycle");
            Logger.warn(`Root template not found: ${rootTemplateName}`, 'EnginePreProcessJson');
            return { html: `<!-- Error: Root template not found: ${appSite}/${rootTemplateName} -->`, operations: null };
        }

        Logger.flow(`MergeTemplates: mainTemplate=found resolvedName=${result.resolvedTemplateName || ''}`, "PreProcessJson.MergeLifecycle");

        // STEP 3: Merge DataJson into HtmlJson BEFORE recursive processing
        // Always merge to apply contextJson values (AppSite, AppView, AppFile) even if template has no DataJson
        const operations: TemplateOperation[] = [];
        // Stage KeyReplace+KeyMissing so MergeTemplate emits them at canonical phase position.
        const rootKeyReplaceStage: TemplateOperation[] = [];
        const rootKeyMissingStage: TemplateOperation[] = [];
        const templateKey = `${rootTemplateAppSite}_${rootTemplateName}`;
        const dataJson = result.dataJson ?? new JsonObject();
        Logger.flow(`MergeTemplates: mainMerge=enter templateKey=${templateKey}`, "PreProcessJson.MergeLifecycle");
        const startJsonMerge = performance.now();
        const mergedHtmlJson = mergeDataJsonIntoHtmlJson(
            result.htmlJson!,
            dataJson,
            templateKey,
            rootTemplateName,
            operations,
            loader.defaultReplaceHtml,
            false,
            contextJson,
            null,
            rootKeyReplaceStage,
            rootKeyMissingStage
        );
        // Only count when template has actual DataJson, to align with PreProcess engine count
        if (result.dataJson) {
            engine._jsonMergeNanos += msToNanos(performance.now() - startJsonMerge);
            engine._jsonMergeCount++;
        }

        // STEP 4: Merge recursively with merged HtmlJson (JsonKey entries already replaced by loader)
        // Note: Slot/Placeholder timing is captured inside mergeTemplate for each individual operation
        let mergedDataJson: JsonObject = dataJson;

        // Merge context JSON into dataJson for array iteration.
        // Arrays use smart merge (append/skip) when ArrayNamePrimary found in contextJson.
        if (contextJson && contextJson.size > 0) {
            mergedDataJson = mergeContextIntoDataJson(mergedDataJson, contextJson);
        }

        Logger.flow(`MergeTemplates: ProcessTemplateContent=enter`, "PreProcessJson.MergeLifecycle");
        // AM-001: bundle the merge-invariant values once; traversal helpers receive them as one context.
        const context: PreProcessJsonMergeContext = {
            rootComponent: metadata.rootComponent,
            appSiteName: appSite,
            metadata,
            genComponentHtml,
            contextJson,
            configJson,
            clientMode
        };
        let mergedHtml = mergeTemplate(
            engine,
            loader,
            context,
            metadata.rootComponent,
            mergedHtmlJson,
            mergedDataJson,
            operations,
            genOperationJson,
            componentHtml,
            0,
            flowStack,
            null,
            rootKeyReplaceStage,
            rootKeyMissingStage,
            null
        );

        // Collect inheritance operations from loader
        const inheritanceOps = loader.getAndClearInheritanceOperations();

        // Build operations metadata if genOperationJson is enabled
        let opsMetadata: OperationsMetadataClass | null = null;
        const builtMetadata = engine.buildOperationsMetadata(
            genOperationJson,
            appSite,
            metadata.appFile,
            metadata.appView,
            templateKey,
            operations,
            inheritanceOps
        );
        if (builtMetadata) {
            const opsMeta = new OperationsMetadataClass();
            opsMeta.appSite = builtMetadata.appSite;
            opsMeta.appFile = builtMetadata.appFile;
            opsMeta.appView = builtMetadata.appView;
            opsMeta.templateName = builtMetadata.templateName;
            opsMeta.operations = builtMetadata.operations;
            opsMeta.hasInheritance = builtMetadata.hasInheritance;
            opsMeta.inheritanceOperations = builtMetadata.inheritanceOperations;
            opsMetadata = opsMeta;
        }

        // Return the root merged HTML + operations metadata to MergeTemplates, which owns the
        // root componentHtml entry and the dynamic-handler-result collection (csharp shape).
        return { html: mergedHtml, operations: opsMetadata };
}
